import AccessTimeIcon from "@mui/icons-material/AccessTime";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import MilitaryTechOutlinedIcon from "@mui/icons-material/MilitaryTechOutlined";
import { Box, Typography } from "@mui/material";
import type { GameListItem } from "../games.api";
import { formatClock, formatDuration, formatPlayedAt, titleCase } from "../review-format";
import { resultColor } from "@/shared/utils/chess";
import { ReviewDataPoint } from "./ReviewDataPoint";
import { ReviewSectionTitle } from "./ReviewSectionTitle";
import { useProfileStore } from "@/features/profile";
import { GamePlayerAvatar } from "./GamePlayerAvatar";
import { gamePerformance } from "../game-performance";

type GameReviewMetadataProps = {
  game: GameListItem;
  opening: string;
  performance?: ReturnType<typeof gamePerformance>;
};

const playerRow = { display: "flex", alignItems: "center", gap: 0.75 };

export const GameReviewMetadata = ({ game, opening, performance = gamePerformance([]) }: GameReviewMetadataProps) => {
  const profile = useProfileStore((state) => state.profile);
  return (
    <>
      <Box sx={{ pb: 2, borderBottom: "1px solid rgba(255,255,255,0.12)" }}>
        <Typography
          sx={{ fontSize: "0.65rem", color: "primary.main", fontWeight: 800, letterSpacing: "0.08em", mb: 0.75 }}
        >
          {game.playerColor === "w" ? "WHITE" : "BLACK"}
        </Typography>
        <Typography sx={{ ...playerRow, fontSize: { xs: "1.45rem", md: "1.8rem" }, lineHeight: 1.05, fontWeight: 750 }}>
          <GamePlayerAvatar username={profile?.username ?? ""} src={profile?.chessCom.avatar} />
          <Box component="span" sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
            You{" "}
            <Box component="span" sx={{ color: "text.secondary", fontWeight: 500 }}>
              ({game.playerRating || "—"})
            </Box>
            <Box component="span" sx={{ color: "text.secondary", fontSize: "caption.fontSize", fontWeight: 500 }}>
              {` Est. accuracy ${performance.player.accuracy === null ? "unavailable" : `${performance.player.accuracy.toFixed(1)}%`}`}
            </Box>
          </Box>
        </Typography>
        <Typography sx={{ color: "text.secondary", fontSize: "0.75rem", my: 0.55 }}>against</Typography>
        <Typography sx={{ ...playerRow, fontSize: { xs: "1.15rem", md: "1.35rem" }, lineHeight: 1.1, fontWeight: 700 }}>
          <GamePlayerAvatar key={game.opponent} username={game.opponent} />
          <Box component="span" sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
            {game.opponent}{" "}
            <Box component="span" sx={{ color: "text.secondary", fontWeight: 500 }}>
              ({game.opponentRating || "—"})
            </Box>
            <Box component="span" sx={{ color: "text.secondary", fontSize: "caption.fontSize", fontWeight: 500 }}>
              {` Est. accuracy ${performance.opponent.accuracy === null ? "unavailable" : `${performance.opponent.accuracy.toFixed(1)}%`}`}
            </Box>
          </Box>
        </Typography>
      </Box>
      <Box sx={{ py: 2, borderBottom: 1, borderColor: "divider" }}>
        <ReviewSectionTitle icon={<InsightsOutlinedIcon />}>Game performance estimate</ReviewSectionTitle>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1.75 }}>
          {(
            [
              ["You", performance.player],
              [game.opponent, performance.opponent],
            ] as const
          ).map(([name, side], index) => (
            <Box key={index} sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
              <ReviewDataPoint label={name} value={side.estimate === null ? "Unavailable" : String(side.estimate)} />
              <Typography sx={{ mt: 0.5, fontSize: "0.65rem", color: "text.secondary" }}>
                {`${name}: ${side.evaluatedMoves} evaluated ${side.evaluatedMoves === 1 ? "move" : "moves"}`}
              </Typography>
            </Box>
          ))}
        </Box>
        <Typography sx={{ mt: 1, fontSize: "0.7rem", color: "text.secondary" }}>
          Rough estimate from this game&apos;s evaluated moves, not an actual or calibrated rating.
        </Typography>
      </Box>

      <Box sx={{ py: 2, borderBottom: 1, borderColor: "divider" }}>
        <ReviewSectionTitle icon={<AccessTimeIcon />}>Clock & date</ReviewSectionTitle>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.75 }}>
          <ReviewDataPoint label="Time control" value={formatClock(game.exactTimeControl, game.timeControl)} />
          <ReviewDataPoint label="Duration" value={formatDuration(game.durationSeconds)} />
          <Box sx={{ gridColumn: "1 / -1" }}>
            <ReviewDataPoint label="Played" value={formatPlayedAt(game.playedAt)} />
          </Box>
        </Box>
      </Box>
      <Box sx={{ py: 2, borderBottom: "1px solid rgba(255,255,255,0.12)" }}>
        <ReviewSectionTitle icon={<MilitaryTechOutlinedIcon />}>Match</ReviewSectionTitle>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.75 }}>
          <ReviewDataPoint
            label="Result"
            value={titleCase(game.resultCode ?? game.result)}
            accent={resultColor(game.result)}
          />
          <ReviewDataPoint
            label="Format"
            value={
              game.rated == null
                ? titleCase(game.rules)
                : `${game.rated ? "Rated" : "Unrated"} ${titleCase(game.rules)}`
            }
          />
          <Box sx={{ gridColumn: "1 / -1" }}>
            <ReviewDataPoint
              label="Opening"
              value={opening}
              href={game.eco?.startsWith("http") ? game.eco : undefined}
            />
          </Box>
          {game.termination && (
            <Box sx={{ gridColumn: "1 / -1" }}>
              <ReviewDataPoint label="Termination" value={game.termination} />
            </Box>
          )}
        </Box>
      </Box>
      <Box sx={{ py: 2, borderBottom: "1px solid rgba(255,255,255,0.12)" }}>
        <ReviewSectionTitle icon={<InsightsOutlinedIcon />}>Review</ReviewSectionTitle>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 1.25 }}>
          <ReviewDataPoint
            label="Estimated rating"
            value={game.estimatedRating != null ? `${game.estimatedRating}` : "—"}
          />
          <ReviewDataPoint
            label={game.chessComAccuracy != null ? "Accuracy · Chess.com" : "Estimated Accuracy · Nikola"}
            value={
              game.chessComAccuracy != null
                ? `${game.chessComAccuracy.toFixed(1)}%`
                : game.engineAccuracy != null
                  ? `${game.engineAccuracy.toFixed(1)}%`
                  : "—"
            }
          />
          <ReviewDataPoint label="Avg loss" value={game.averageCpl != null ? game.averageCpl.toFixed(1) : "—"} />
          <ReviewDataPoint
            label="Mistakes"
            value={String(game.mistakeCount ?? 0)}
            accent={game.mistakeCount ? "#f97316" : undefined}
          />
          <ReviewDataPoint
            label="Blunders"
            value={String(game.blunderCount ?? 0)}
            accent={game.blunderCount ? "#ef4444" : undefined}
          />
        </Box>
      </Box>
    </>
  );
};
